import type {
  IIEMService,
  IEMCustomer,
  PHRItem,
  THRItem,
  IEItem,
  ServiceMetrics,
  PersonSizingMeasurement,
  SizingData,
  BilletData,
  ExchangeReplacementOption,
  ExchangeCompletionRequestItem,
  ExchangeCompletionResponse,
  IEMAdditionalItemGroup,
  IEMBuyProgramCatalogItem,
  ThrConversionRequest,
  ThrConversionResponse,
  ThrDueDateUpdateRequest,
  ThrDueDateUpdateResponse,
  AllocationSizeConversionRequest,
  AllocationSizeConversionResponse,
  AllocationSizeConversionBatchRequest,
  AllocationSizeConversionBatchResponse,
  IeKitRemovedAssetsRequest,
  IeKitRemovedAssetsResponse,
  IeKitQuantityDecreasedAssetsRequest,
  IeKitQuantityDecreasedAssetsResponse,
  IeKitAddedAssetsRequest,
  IeKitAddedAssetsResponse,
  PersonIeKitAssignmentRequest,
  PersonIeKitAssignmentResponse,
  CatalogSizingOption,
  CompleteIssueRequest,
  CompleteIssueResponse,
  IEMSignatureDocumentPayload,
  IndividualStateResponse,
} from './types';
import { httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/api';
import { entityService } from '@/services/EntityService';
import type { CreateBulkEntityRequest, Entity } from '@/types/Entity';
import { buildPostgrestOrIlikeFilter } from '@/utils/postgrestFilters';
import { isPnpCatalogEntity } from '@/utils/iemFilters';
import { prepareBuyProgramAssetData } from './assetUtils';
import { formatNomenclatureWithSize } from '@equip/utils';

type RawHandReceiptItem = {
  id: string;
  person_id?: string | null;
  asset_id?: string | null;
  item?: string | null;
  nsn?: string | null;
  catalog_id?: string | null;
  parent_catalog_id?: string | null;
  lin?: string | null;
  ie_kit_id?: string | null;
  ie_kit_name?: string | null;
  serial_number?: string | null;
  issueDate?: string | null;
  condition?: string | null;
  status?: string | null;
  type?: string | null;
  recoverable?: boolean | null;
  extension_due_date?: string | null;
  extension_reason?: string | null;
  extension_approval_reference?: string | null;
  extension_approved_at?: string | null;
  extension_approved_by?: string | null;
  extension_approved_by_name?: string | null;
  has_extension?: boolean | null;
  extension_metadata?: RawThrExtension | null;
  // Buy Program pricing fields
  actual_price?: number | null;
  tax?: number | null;
  shipping?: number | null;
  // Expiration date from asset entity_data
  expiration_date?: string | null;
  // Catalog-level unit price (fallback when actual_price is NULL)
  unit_price?: number | null;
};

type RawThrItem = RawHandReceiptItem & {
  qty?: number | null;
  dueDate?: string | null;
  recoverable?: boolean | null;
  nonrecoverable_reason?: string | null;
  extension_due_date?: string | null;
  extension_reason?: string | null;
  extension_approved_at?: string | null;
  extension_approved_by?: string | null;
  extension_approved_by_name?: string | null;
  has_extension?: boolean | null;
  extension_metadata?: RawThrExtension | null;
};

type RawThrExtension = {
  due_date?: string | null;
  reason?: string | null;
  approval_reference?: string | null;
  approvalReference?: string | null;
  approved_at?: string | null;
  approved_by?: string | null;
  approved_by_name?: string | null;
};

type ApiSizingEntry = {
  sizing_id?: string | null;
  sizing_identifier?: string | null;
  person_id?: string | null;
  catalog_id?: string | null;
  catalog_identifier?: string | null;
  catalog_name?: string | null;
  catalog_lin?: string | null;
  catalog_nsn?: string | null;
  catalog_type?: string | null;
  parent_catalog_id?: string | null;
  parent_catalog_identifier?: string | null;
  parent_catalog_name?: string | null;
};

const normaliseHandReceiptDate = (value?: string | null): string => {
  if (value == null) {
    return '--';
  }

  const trimmed = value.trim();
  if (!trimmed || trimmed === '--') {
    return '--';
  }

  const isoDateMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDateMatch && isoDateMatch[1]) {
    return isoDateMatch[1];
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return trimmed;
  }

  return parsed.toISOString().slice(0, 10);
};

const normalizeOptionalDate = (value?: string | null): string | null => {
  const normalized = normaliseHandReceiptDate(value);
  return normalized === '--' ? null : normalized;
};

const coalesceText = (...values: Array<unknown>): string | null => {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
  }
  return null;
};

const toBooleanFlag = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', 't', 'yes', 'y', '1'].includes(normalized)) {
      return true;
    }
    if (['false', 'f', 'no', 'n', '0'].includes(normalized)) {
      return false;
    }
  }
  return null;
};

const mapSizingEntry = (entry: ApiSizingEntry): PersonSizingMeasurement | null => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const sizingId = coalesceText(entry.sizing_id);
  const catalogId = coalesceText(entry.catalog_id);
  const personId = coalesceText(entry.person_id);

  if (!sizingId || !catalogId || !personId) {
    return null;
  }

  return {
    id: sizingId,
    identifier: coalesceText(entry.sizing_identifier),
    personId,
    catalogId,
    catalogIdentifier: coalesceText(entry.catalog_identifier),
    catalogName: coalesceText(entry.catalog_name),
    catalogLin: coalesceText(entry.catalog_lin),
    catalogNsn: coalesceText(entry.catalog_nsn),
    catalogType: coalesceText(entry.catalog_type),
    parentCatalogId: coalesceText(entry.parent_catalog_id),
    parentCatalogIdentifier: coalesceText(entry.parent_catalog_identifier),
    parentCatalogName: coalesceText(entry.parent_catalog_name),
  };
};

const buildThrExtensionInfo = (
  item: RawThrItem,
): { extension: THRItem['extension']; hasExtension: boolean } => {
  const extensionSource = item.extension_metadata ?? null;

  const extensionDueDateRaw = coalesceText(extensionSource?.due_date, item.extension_due_date);

  const extensionReason = coalesceText(extensionSource?.reason, item.extension_reason);

  const extensionApprovalReference = coalesceText(
    extensionSource?.approval_reference,
    extensionSource?.approvalReference,
    item.extension_approval_reference,
  );

  const extensionApprovedAtRaw = coalesceText(
    extensionSource?.approved_at,
    item.extension_approved_at,
  );

  const extensionApprovedBy = coalesceText(
    extensionSource?.approved_by,
    item.extension_approved_by,
  );

  const extensionApprovedByName = coalesceText(
    extensionSource?.approved_by_name,
    item.extension_approved_by_name,
  );

  const explicitFlag = toBooleanFlag(item.has_extension);
  const hasData =
    Boolean(extensionDueDateRaw) ||
    Boolean(extensionReason) ||
    Boolean(extensionApprovalReference) ||
    Boolean(extensionApprovedAtRaw) ||
    Boolean(extensionApprovedBy);

  const hasExtension = explicitFlag ?? hasData;

  if (!hasExtension) {
    return { extension: null, hasExtension: false };
  }

  const normalizedDueDate = normalizeOptionalDate(extensionDueDateRaw);

  return {
    extension: {
      dueDate: normalizedDueDate,
      reason: extensionReason,
      approvalReference: extensionApprovalReference,
      approvedAt: extensionApprovedAtRaw,
      approvedBy: extensionApprovedBy,
      approvedByName: extensionApprovedByName,
    },
    hasExtension: true,
  };
};

export class IEMService implements IIEMService {
  async lookupCustomer(badgeId: string): Promise<IEMCustomer> {
    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_CUSTOMER_LOOKUP, {
      query: badgeId,
    });
  }

  async lookupByIndividualId(individualId: string): Promise<IEMCustomer> {
    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_CUSTOMER_LOOKUP, {
      individual_id: individualId,
    });
  }

  async clearIndividual(individualId: string): Promise<IndividualStateResponse> {
    return httpClient.post(API_CONFIG.ENDPOINTS.INDIVIDUAL_CLEAR, {
      individual_id: individualId,
    });
  }

  async reactivateIndividual(individualId: string): Promise<IndividualStateResponse> {
    return httpClient.post(API_CONFIG.ENDPOINTS.INDIVIDUAL_REACTIVATE, {
      individual_id: individualId,
    });
  }

  async getIndividualState(individualId: string): Promise<IndividualStateResponse> {
    return httpClient.get(
      `${API_CONFIG.ENDPOINTS.INDIVIDUAL_STATE}/${encodeURIComponent(individualId)}`,
    );
  }

  async checkAvailability(items: Array<{ nsn: string; qty: number }>): Promise<{
    results: Array<{ nsn: string; available: boolean; on_hand: number }>;
  }> {
    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_AVAILABILITY_CHECK, { items });
  }

  async checkAvailabilityByCatalogIds(
    items: Array<{ catalogId: string; qty: number }>,
    locationId?: string,
  ): Promise<{
    results: Array<{ catalogId: string; available: boolean; on_hand: number; requested: number }>;
  }> {
    const payload: Record<string, unknown> = { items };
    if (locationId) {
      payload.locationId = locationId;
    }
    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_AVAILABILITY_CHECK_BY_CATALOG_IDS, payload);
  }

  async getPHRItems(personId: string, params: Record<string, string> = {}): Promise<PHRItem[]> {
    if (!personId) {
      return [];
    }

    const queryParams = { person_id: `eq.${personId}`, select: '*,type', ...params };
    const query = new URLSearchParams(queryParams).toString();
    const response = (await httpClient.get(`/api/v1/phr_items?${query}`)) as RawHandReceiptItem[];

    return response.map((item) => {
      const { extension, hasExtension } = buildThrExtensionInfo(item);
      return {
        id: item.id,
        person_id: item.person_id ?? undefined,
        asset_id: item.asset_id ?? undefined,
        item: item.item ?? 'Unknown Item',
        nsn: item.nsn ?? '—',
        catalogId: item.catalog_id ?? null,
        parentCatalogId: item.parent_catalog_id ?? null,
        lin: item.lin ?? null,
        ie_kit_id: item.ie_kit_id ?? null,
        ie_kit_name: item.ie_kit_name ?? null,
        serial_number: item.serial_number ?? null,
        issueDate: normaliseHandReceiptDate(item.issueDate ?? undefined),
        condition: item.condition ?? 'Unknown',
        status: item.status ?? 'Assigned',
        type: item.type ?? undefined,
        recoverable: item.recoverable ?? null,
        hasExtension,
        extension,
        // Buy Program pricing fields
        actualPrice: item.actual_price ?? null,
        tax: item.tax ?? null,
        shipping: item.shipping ?? null,
        expirationDate: item.expiration_date ?? null,
        unitPrice: item.unit_price ?? null,
      } satisfies PHRItem;
    });
  }

  async getTHRItems(personId: string, params: Record<string, string> = {}): Promise<THRItem[]> {
    if (!personId) {
      return [];
    }

    const queryParams = { person_id: `eq.${personId}`, select: '*,type', ...params };
    const query = new URLSearchParams(queryParams).toString();
    const response = (await httpClient.get(`/api/v1/thr_items?${query}`)) as RawThrItem[];

    return response.map((item) => {
      const { extension, hasExtension } = buildThrExtensionInfo(item);
      return {
        id: item.id,
        person_id: item.person_id ?? undefined,
        asset_id: item.asset_id ?? undefined,
        item: item.item ?? 'Unknown Item',
        nsn: item.nsn ?? '—',
        catalogId: item.catalog_id ?? null,
        parentCatalogId: item.parent_catalog_id ?? null,
        lin: item.lin ?? null,
        ie_kit_id: item.ie_kit_id ?? null,
        ie_kit_name: item.ie_kit_name ?? null,
        qty: item.qty ?? 0,
        issueDate: normaliseHandReceiptDate(item.issueDate ?? undefined),
        dueDate: normaliseHandReceiptDate(item.dueDate ?? undefined),
        status: item.status ?? 'Active',
        recoverable: item.recoverable ?? null,
        nonrecoverable_reason: item.nonrecoverable_reason ?? null,
        type: item.type ?? undefined,
        hasExtension,
        extension,
        expirationDate: item.expiration_date ?? null,
        actualPrice: item.actual_price ?? null,
        unitPrice: item.unit_price ?? null,
      } satisfies THRItem;
    });
  }

  async getIESummary(personId: string, params: Record<string, string> = {}): Promise<IEItem[]> {
    const queryParams = { person_id: `eq.${personId}`, ...params };
    const query = new URLSearchParams(queryParams).toString();
    const result = (await httpClient.get(`/api/v1/ie_summary?${query}`)) as Array<{
      item: string;
      nsn: string;
      lin?: string | null;
      authorized_qty?: number | null;
      current_qty?: number | null;
      status: string;
      kit_name?: string | null;
      optional?: boolean | null;
      notes?: string | null;
      person_ie_kit_id?: string | null;
      kit_item_id?: string | null;
      ie_kit_id?: string | null;
      catalog_id?: string | null;
      kit_assignment_name?: string | null;
      available_qty?: number | null;
      catalog_type?: string | null;
      expiration_duration_from_start_date?: number | null;
    }>;

    return result.map((entry) => ({
      item: entry.item,
      nsn: entry.nsn,
      lin: entry.lin ?? null,
      authorizedQty: entry.authorized_qty ?? 0,
      currentQty: entry.current_qty ?? 0,
      status: entry.status,
      kitAssignmentName: entry.kit_assignment_name ?? null,
      kitName: entry.kit_name ?? null,
      optional: entry.optional ?? false,
      notes: entry.notes ?? null,
      personIeKitId: entry.person_ie_kit_id ?? null,
      kitItemId: entry.kit_item_id ?? null,
      ieKitId: entry.ie_kit_id ?? null,
      catalogId: entry.catalog_id ?? null,
      catalogType: entry.catalog_type ?? null,
      availableQty: entry.available_qty ?? 0,
      expirationDurationDays: entry.expiration_duration_from_start_date ?? null,
    }));
  }

  async getServiceMetrics(): Promise<ServiceMetrics> {
    const response = (await httpClient.get('/api/v1/service_metrics')) as Array<{
      avg_service_time?: string;
      customers_today?: number;
      efficiency?: string;
    }>;

    const metrics = Array.isArray(response) ? response[0] : undefined;

    return {
      avgServiceTime: metrics?.avg_service_time ?? '--',
      customersToday: metrics?.customers_today ?? 0,
      efficiency: metrics?.efficiency ?? '--',
    };
  }

  async getSizingForCustomer(customerId: string): Promise<SizingData | null> {
    const query = new URLSearchParams({ customer_id: `eq.${customerId}` }).toString();
    const result = (await httpClient.get(`/api/v1/customers?${query}`)) as Array<{
      sizing?: ApiSizingEntry[];
    }>;

    if (!Array.isArray(result) || result.length === 0) {
      return null;
    }

    const rawEntries = result[0]?.sizing;
    if (!Array.isArray(rawEntries) || rawEntries.length === 0) {
      return [];
    }

    const mapped = rawEntries
      .map((entry) => mapSizingEntry(entry))
      .filter((entry): entry is PersonSizingMeasurement => entry !== null);

    return mapped;
  }

  async getBilletForCustomer(customerId: string): Promise<BilletData | null> {
    const query = new URLSearchParams({ customer_id: `eq.${customerId}` }).toString();
    const result = (await httpClient.get(`/api/v1/customers?${query}`)) as Array<{
      billet?: BilletData;
    }>;
    return result[0]?.billet || null;
  }

  async getAvailableUniformSizes(): Promise<string[]> {
    const result = (await httpClient.get('/api/v1/uniform_sizes')) as Array<{ size: string }>;
    return result.map((r) => r.size);
  }

  async getAvailableBootSizes(): Promise<string[]> {
    const result = (await httpClient.get('/api/v1/boot_sizes')) as Array<{ size: string }>;
    return result.map((r) => r.size);
  }

  async getAvailableHelmetSizes(): Promise<string[]> {
    const result = (await httpClient.get('/api/v1/helmet_sizes')) as Array<{ size: string }>;
    return result.map((r) => r.size);
  }

  async getAvailableGloveSizes(): Promise<string[]> {
    const result = (await httpClient.get('/api/v1/glove_sizes')) as Array<{ size: string }>;
    return result.map((r) => r.size);
  }

  async getCatalogChildren(parentCatalogIds: string[]): Promise<CatalogSizingOption[]> {
    const uniqueParentIds = Array.from(
      new Set(
        parentCatalogIds
          .map((id) => (typeof id === 'string' ? id.trim() : ''))
          .filter((id): id is string => Boolean(id && id.length > 0)),
      ),
    );

    if (uniqueParentIds.length === 0) {
      return [];
    }

    // Batch into groups of 50 to avoid URL length limits with large catalogs
    const BATCH_SIZE = 50;
    const batches: string[][] = [];
    for (let i = 0; i < uniqueParentIds.length; i += BATCH_SIZE) {
      batches.push(uniqueParentIds.slice(i, i + BATCH_SIZE));
    }

    const batchResults = await Promise.all(
      batches.map((batch) => {
        const parentFilter = batch.length === 1 ? `eq.${batch[0]}` : `in.(${batch.join(',')})`;
        return entityService.list({
          type: 'catalog',
          limit: Math.max(batch.length * 25, 100),
          filters: {
            'entity_data->>parent_id': parentFilter,
          },
          order: 'display_name.asc',
        });
      }),
    );

    const catalogs = batchResults.flat();

    return catalogs.reduce<CatalogSizingOption[]>((acc, catalog) => {
      const entityData = (catalog.entity_data ?? {}) as Record<string, unknown>;
      
      // Exclude retired items from Front Window
      if (catalog.status === 'retired') {
        return acc;
      }
      
      const rawParentId = entityData.parent_id;
      const parentCatalogId =
        typeof rawParentId === 'string' && rawParentId.trim().length > 0
          ? rawParentId.trim()
          : null;

      if (!parentCatalogId) {
        return acc;
      }

      const lin =
        typeof entityData.lin === 'string' && entityData.lin.trim().length > 0
          ? entityData.lin.trim()
          : null;
      const nsn =
        typeof entityData.nsn === 'string' && entityData.nsn.trim().length > 0
          ? entityData.nsn.trim()
          : null;
      const sizeLabel =
        typeof entityData.size === 'string' && entityData.size.trim().length > 0
          ? entityData.size.trim()
          : null;

      acc.push({
        id: catalog.id,
        parentCatalogId,
        entityIdentifier:
          typeof catalog.entity_identifier === 'string' &&
          catalog.entity_identifier.trim().length > 0
            ? catalog.entity_identifier
            : null,
        displayName: catalog.display_name,
        lin,
        nsn,
        sizeLabel,
      });
      return acc;
    }, []);
  }

  /**
   * Fetches ALL catalog children (entities with a parent_id) using a small number of
   * paginated requests rather than batching by specific parent IDs. Use this when
   * the parent set is large (e.g. the full additional-items catalog).
   */
  async getAllCatalogChildren(): Promise<CatalogSizingOption[]> {
    const PAGE_SIZE = 1000;
    const allCatalogs: Entity[] = [];
    let offset = 0;
    while (true) {
      const page = await entityService.list({
        type: 'catalog',
        limit: PAGE_SIZE,
        offset,
        filters: { 'entity_data->>parent_id': 'not.is.null' },
        order: 'display_name.asc',
      });
      allCatalogs.push(...page);
      if (page.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    return allCatalogs.reduce<CatalogSizingOption[]>((acc, catalog) => {
      const entityData = (catalog.entity_data ?? {}) as Record<string, unknown>;
      
      // Exclude retired items from Front Window
      if (catalog.status === 'retired') {
        return acc;
      }
      
      const rawParentId = entityData.parent_id;
      const parentCatalogId =
        typeof rawParentId === 'string' && rawParentId.trim().length > 0
          ? rawParentId.trim()
          : null;

      if (!parentCatalogId) return acc;

      const lin =
        typeof entityData.lin === 'string' && entityData.lin.trim().length > 0
          ? entityData.lin.trim()
          : null;
      const nsn =
        typeof entityData.nsn === 'string' && entityData.nsn.trim().length > 0
          ? entityData.nsn.trim()
          : null;
      const sizeLabel =
        typeof entityData.size === 'string' && entityData.size.trim().length > 0
          ? entityData.size.trim()
          : null;

      acc.push({
        id: catalog.id,
        parentCatalogId,
        entityIdentifier:
          typeof catalog.entity_identifier === 'string' &&
          catalog.entity_identifier.trim().length > 0
            ? catalog.entity_identifier
            : null,
        displayName: catalog.display_name,
        lin,
        nsn,
        sizeLabel,
      });
      return acc;
    }, []);
  }

  async getExchangeReplacementOptions({
    catalogGroupIds,
    excludeAssetIds = [],
  }: {
    catalogGroupIds: string[];
    excludeAssetIds?: string[];
  }): Promise<ExchangeReplacementOption[]> {
    const uniqueGroupIds = Array.from(
      new Set(
        catalogGroupIds
          .map((id) => id?.trim())
          .filter((id): id is string => Boolean(id)),
      ),
    );

    if (uniqueGroupIds.length === 0) {
      return [];
    }

    const excludeSet = new Set(
      excludeAssetIds
        .map((assetId) => assetId?.trim())
        .filter((assetId): assetId is string => Boolean(assetId)),
    );

    const groupIdFilter = `in.(${uniqueGroupIds.join(',')})`;

    // 1. Fetch Catalog Items (to show all options, even out of stock).
    // Match by nomenclature group (parent catalog ID) rather than LIN so that
    // items whose LIN changed since issue are still found. Fetch:
    //   a) Sized children whose parent_id is one of the group IDs
    //   b) The group roots (parent/unsized catalog items) by their own ID
    const [childCatalogs, rootCatalogs] = await Promise.all([
      entityService.list({
        type: 'catalog',
        limit: 1000,
        filters: { 'entity_data->>parent_id': groupIdFilter },
      }),
      entityService.list({
        type: 'catalog',
        limit: 1000,
        filters: { id: groupIdFilter },
      }),
    ]);

    // Merge, deduplicating by ID
    const catalogById = new Map<string, (typeof rootCatalogs)[number]>();
    for (const c of [...rootCatalogs, ...childCatalogs]) {
      catalogById.set(c.id, c);
    }
    const catalogItems = Array.from(catalogById.values());

    // 2. Fetch Available Assets filtered by the catalog IDs discovered above.
    // Filtering by catalog_id (instead of LIN) keeps the result scoped to the
    // same nomenclature family regardless of what LIN is recorded on the asset.
    const allCatalogIds = catalogItems.map((c) => c.id);
    const assets =
      allCatalogIds.length === 0
        ? []
        : await this.getAssets({
            filters: { 'entity_data->>catalog_id': `in.(${allCatalogIds.join(',')})` },
            limit: 10000,
            requireAvailable: true,
          });

    const aggregated = new Map<string, ExchangeReplacementOption>();

    // Build set of PnP catalog IDs — excluded from exchange replacement options
    const pnpCatalogIds = new Set(catalogItems.filter(isPnpCatalogEntity).map((c) => c.id));

    // Build a parent-to-children map so assets linked to a nonsized parent
    // catalog can be fanned out to every sized child option.  This mirrors the
    // fan-out logic in iem-available-assets.ts used by the IE Kit issue tab.
    const parentToChildIds = new Map<string, string[]>();
    for (const catalog of catalogItems) {
      const entityData = catalog.entity_data;
      const parentId =
        typeof entityData.parent_id === 'string' && entityData.parent_id.trim().length > 0
          ? entityData.parent_id.trim()
          : null;
      if (parentId) {
        const children = parentToChildIds.get(parentId) ?? [];
        children.push(catalog.id);
        parentToChildIds.set(parentId, children);
      }
    }

    // Initialize options from non-PnP catalog items
    for (const catalog of catalogItems) {
      const entityData = catalog.entity_data;
      const lin = typeof entityData.lin === 'string' ? entityData.lin.trim() || '—' : '—';

      if (pnpCatalogIds.has(catalog.id)) continue;

      const nsn = typeof entityData.nsn === 'string' ? entityData.nsn.trim() : '—';
      const size = typeof entityData.size === 'string' ? entityData.size : undefined;
      const baseNomenclature = catalog.display_name || nsn;
      const name = formatNomenclatureWithSize(baseNomenclature, size);
      const parentCatalogId =
        typeof entityData.parent_id === 'string' ? entityData.parent_id : null;

      // Use catalog ID as the unique identifier
      const id = catalog.id;

      aggregated.set(id, {
        id,
        lin,
        nsn,
        parentCatalogId,
        baseNomenclature,
        name,
        stock: 0,
        assetIds: [],
      });
    }

    // Fill in stock from Assets
    for (const asset of assets) {
      if (excludeSet.has(asset.id)) {
        continue;
      }

      const entityData = asset.entity_data ?? {};
      const catalogId = typeof entityData.catalog_id === 'string' ? entityData.catalog_id : null;

      // Skip PnP assets — their catalog is excluded from exchange replacement options
      if (catalogId && pnpCatalogIds.has(catalogId)) continue;

      // If the asset is linked to a parent catalog that has sized children,
      // fan the asset out to every child option so the sized rows show stock.
      const childIds = catalogId ? parentToChildIds.get(catalogId) : undefined;
      if (childIds && childIds.length > 0) {
        for (const childId of childIds) {
          if (pnpCatalogIds.has(childId)) continue;
          const option = aggregated.get(childId);
          if (option) {
            option.assetIds.push(asset.id);
            option.stock = option.assetIds.length;
          }
        }
      } else if (catalogId && aggregated.has(catalogId)) {
        const option = aggregated.get(catalogId)!;
        option.assetIds.push(asset.id);
        option.stock = option.assetIds.length;
      } else {
        // Fallback for assets without a matching catalog item
        const lin = typeof entityData.lin === 'string' ? entityData.lin.trim() : null;
        if (!lin) continue;

        const nsn = typeof entityData.nsn === 'string' ? entityData.nsn.trim() : '—';
        const assetSize = typeof entityData.size === 'string' ? entityData.size : undefined;
        const name = formatNomenclatureWithSize(
          typeof asset.display_name === 'string' ? asset.display_name : nsn,
          assetSize,
        );
        const key = `${lin}::${nsn}::${name}`;

        if (!aggregated.has(key)) {
          aggregated.set(key, {
            id: key,
            lin,
            nsn,
            parentCatalogId: null,
            baseNomenclature: name,
            name,
            stock: 0,
            assetIds: [],
          });
        }

        const option = aggregated.get(key)!;
        option.assetIds.push(asset.id);
        option.stock = option.assetIds.length;
      }
    }

    const sorted = Array.from(aggregated.values()).sort((a, b) => {
      if (a.lin === b.lin) {
        return a.name.localeCompare(b.name);
      }
      return a.lin.localeCompare(b.lin);
    });

    return sorted;
  }

  async completeIssue(request: CompleteIssueRequest): Promise<CompleteIssueResponse> {
    // IEM complete-issue workflow can take longer than the default HTTP timeout
    // due to document generation (PDF rendering ~10s) and multiple PostgREST calls.
    // Temporal activity timeout is 2 minutes; 60s covers observed worst-case runs.
    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_COMPLETE_ISSUE, request, {
      timeout: 60000,
    });
  }

  async completeTurnIn(request: {
    memberId: string;
    items: Array<{
      handReceiptId: string;
      assetId: string;
      disposition: 'recoverable' | 'non_recoverable' | 'cleared';
    }>;
    documentPayload?: IEMSignatureDocumentPayload;
    startedAt?: string;
    sourceDocumentNumber?: string;
    lossRecordId?: string;
  }): Promise<{
    memberId: string;
    returnedCount: number;
    closedReceiptIds: string[];
    recoverableCount: number;
    nonRecoverableCount: number;
    clearedCount: number;
    transactionLogIds: string[];
    documentNumber?: string;
  }> {
    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_COMPLETE_TURN_IN, request);
  }

  async completeExchange(request: {
    memberId: string;
    performedBy?: string;
    items: ExchangeCompletionRequestItem[];
    documentPayload?: IEMSignatureDocumentPayload;
    startedAt?: string;
  }): Promise<ExchangeCompletionResponse> {
    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_COMPLETE_EXCHANGE, request);
  }

  async getAdditionalItemGroups({
    searchTerm,
    limit = 1000,
    offset = 0,
  }: {
    searchTerm?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<IEMAdditionalItemGroup[]> {
    type ViewRow = {
      group_id: string | null;
      lin?: string | null;
      nsn?: string | null;
      name?: string | null;
      catalog_identifier?: string | null;
      available_qty?: number | null;
      asset_ids?: string[] | null;
    };

    const p = new URLSearchParams({ order: 'lin.asc,name.asc' });
    p.set('limit', String(limit));
    if (offset > 0) p.set('offset', String(offset));

    const trimmedSearch = searchTerm?.trim();
    if (trimmedSearch && trimmedSearch.length >= 2) {
      const normalized = trimmedSearch.replace(/\s+/g, ' ').trim();
      p.set(
        'or',
        buildPostgrestOrIlikeFilter(['name', 'lin', 'nsn', 'catalog_identifier'], normalized),
      );
    }

    const result = (await httpClient.get(
      `/api/v1/iem_available_item_groups?${p.toString()}`,
    )) as ViewRow[];

    return result.map((entry) => ({
      groupId: entry.group_id ?? 'unknown-group',
      lin: entry.lin ?? null,
      nsn: entry.nsn ?? null,
      name: entry.name ?? 'Unknown item',
      catalogIdentifier: entry.catalog_identifier ?? null,
      availableQty: Math.max(entry.available_qty ?? 0, 0),
      assetIds: Array.isArray(entry.asset_ids) ? entry.asset_ids : [],
    }));
  }

  async getBuyProgramCatalog({
    searchTerm,
    limit,
    order,
  }: {
    searchTerm?: string;
    limit?: number;
    order?: string;
  } = {}): Promise<IEMBuyProgramCatalogItem[]> {
    const filters: Record<string, string> = {
      'entity_data->>type': 'eq.buy program',
    };

    const trimmedSearch = searchTerm?.trim();
    if (trimmedSearch && trimmedSearch.length >= 2) {
      filters.or = buildPostgrestOrIlikeFilter(
        ['display_name', 'entity_identifier', 'entity_data->>lin', 'entity_data->>nsn'],
        trimmedSearch,
      );
    }

    const entities = await entityService.list({
      type: 'catalog',
      limit,
      order: order ?? 'display_name.asc',
      filters,
    });

    return entities.map((entity) => {
      const entityData = { ...(entity.entity_data ?? {}) } as Record<string, unknown>;
      const lin = typeof entityData.lin === 'string' ? entityData.lin : null;
      const nsn = typeof entityData.nsn === 'string' ? entityData.nsn : null;
      const name =
        typeof entityData.name === 'string' && entityData.name.trim().length > 0
          ? entityData.name
          : entity.display_name;

      let unitPrice: number | null = null;
      const unitPriceRaw = entityData.unit_price;
      if (typeof unitPriceRaw === 'number') {
        unitPrice = Number.isFinite(unitPriceRaw) ? unitPriceRaw : null;
      } else if (typeof unitPriceRaw === 'string') {
        const parsed = Number.parseFloat(unitPriceRaw);
        unitPrice = Number.isFinite(parsed) ? parsed : null;
      }

      const loa =
        typeof entityData.loa === 'string' && entityData.loa.trim().length > 0
          ? entityData.loa.trim()
          : null;

      return {
        id: entity.id,
        entityIdentifier:
          typeof entity.entity_identifier === 'string' && entity.entity_identifier.length > 0
            ? entity.entity_identifier
            : null,
        name,
        displayName: entity.display_name,
        lin,
        nsn,
        unitPrice,
        loa,
        status: entity.status,
        entityData,
      };
    });
  }

  async createBuyProgramAssetFromCatalog({
    catalogId,
    catalogIdentifier,
    displayName,
    entityData,
    actorId,
    actualPrice,
    tax,
    shipping,
  }: {
    catalogId: string;
    catalogIdentifier?: string | null;
    displayName: string;
    entityData: Record<string, unknown>;
    actorId?: string;
    actualPrice?: number;
    tax?: number;
    shipping?: number;
  }): Promise<Entity> {
    const assetData = prepareBuyProgramAssetData({
      catalogId,
      catalogIdentifier: catalogIdentifier ?? null,
      sourceData: entityData,
      actualPrice,
      tax,
      shipping,
    });

    const metadata: Record<string, unknown> = {
      source: 'buy-program-issue',
      catalog_entity_id: catalogId,
    };

    if (catalogIdentifier) {
      metadata.catalog_entity_identifier = catalogIdentifier;
    }

    const requests: CreateBulkEntityRequest[] = [
      {
        entity_type: 'asset',
        display_name: displayName,
        entity_data: assetData,
        metadata,
        status: 'active',
      },
    ];

    const created = await entityService.createBulkEntities(requests, actorId);
    const asset = created[0];

    if (!asset) {
      throw new Error('Failed to create buy program asset');
    }

    return asset;
  }

  async getAssets({
    searchTerm,
    filters = {},
    limit = 50,
    order = 'entity_identifier.asc',
    requireAvailable = false,
    onlyUnassigned = false,
  }: {
    searchTerm?: string;
    filters?: Record<string, string>;
    limit?: number;
    order?: string;
    requireAvailable?: boolean;
    onlyUnassigned?: boolean;
  } = {}): Promise<Entity[]> {
    const mergedFilters: Record<string, string> = { ...filters };

    const trimmedSearch = searchTerm?.trim();
    if (trimmedSearch && trimmedSearch.length >= 2 && !('or' in mergedFilters)) {
      mergedFilters.or = buildPostgrestOrIlikeFilter(
        ['display_name', 'entity_identifier', 'entity_data->>nsn', 'entity_data->>rcn'],
        trimmedSearch,
      );
    }

    const shouldUseAvailableView = requireAvailable || onlyUnassigned;

    if (shouldUseAvailableView) {
      return this.getAvailableAssetsFromView({
        filters: mergedFilters,
        limit,
        order,
      });
    }

    return entityService.list({
      type: 'asset',
      limit,
      filters: mergedFilters,
      order,
    });
  }

  async getAvailableAssetsByCatalogIds({
    catalogIds,
    limit = 1000,
    locationId,
  }: {
    catalogIds: string[];
    limit?: number;
    locationId?: string;
  }): Promise<Entity[]> {
    const normalizedCatalogIds = catalogIds
      .map((id) => (typeof id === 'string' ? id.trim() : ''))
      .filter((id) => id.length > 0);

    if (normalizedCatalogIds.length === 0) {
      return [];
    }

    const payload: Record<string, unknown> = { catalogIds: normalizedCatalogIds, limit };
    if (locationId) {
      payload.locationId = locationId;
    }

    const response = await httpClient.post<{ assets: Entity[] }>(
      API_CONFIG.ENDPOINTS.IEM_AVAILABLE_ASSETS_BY_CATALOG_IDS,
      payload,
    );

    return response.assets;
  }

  /**
   * Lightweight endpoint that returns aggregated counts per (catalogId, locationId)
   * instead of full entity objects. Dramatically reduces memory and network usage.
   */
  async getAvailableAssetCountsByCatalogIds({
    catalogIds,
    locationId,
  }: {
    catalogIds: string[];
    locationId?: string;
  }): Promise<Record<string, Record<string, number>>> {
    const normalizedCatalogIds = catalogIds
      .map((id) => (typeof id === 'string' ? id.trim() : ''))
      .filter((id) => id.length > 0);

    if (normalizedCatalogIds.length === 0) {
      return {};
    }

    const payload: Record<string, unknown> = { catalogIds: normalizedCatalogIds };
    if (locationId) {
      payload.locationId = locationId;
    }

    const response = await httpClient.post<{ counts: Record<string, Record<string, number>> }>(
      API_CONFIG.ENDPOINTS.IEM_AVAILABLE_ASSET_COUNTS_BY_CATALOG_IDS,
      payload,
    );

    return response.counts;
  }

  /**
   * Like getAvailableAssetCountsByCatalogIds but includes assets reserved in a
   * workflow cart. Use this for location label display (how many are physically
   * at the location) rather than for issuance gating (how many can still be added
   * to a new cart).
   */
  async getOnhandAssetCountsByCatalogIds({
    catalogIds,
    locationId,
  }: {
    catalogIds: string[];
    locationId?: string;
  }): Promise<Record<string, Record<string, number>>> {
    const normalizedCatalogIds = catalogIds
      .map((id) => (typeof id === 'string' ? id.trim() : ''))
      .filter((id) => id.length > 0);

    if (normalizedCatalogIds.length === 0) {
      return {};
    }

    const payload: Record<string, unknown> = { catalogIds: normalizedCatalogIds };
    if (locationId) {
      payload.locationId = locationId;
    }

    const response = await httpClient.post<{ counts: Record<string, Record<string, number>> }>(
      API_CONFIG.ENDPOINTS.IEM_ONHAND_ASSET_COUNTS_BY_CATALOG_IDS,
      payload,
    );

    return response.counts;
  }

  async getAvailableAssetsByCatalogId({
    catalogId,
    limit = 10,
  }: {
    catalogId: string;
    limit?: number;
  }): Promise<Entity[]> {
    const normalizedCatalogId = catalogId?.trim();
    if (!normalizedCatalogId) {
      return [];
    }

    return this.getAssets({
      filters: { 'entity_data->>catalog_id': `eq.${normalizedCatalogId}` },
      limit,
      requireAvailable: true,
      onlyUnassigned: true,
    });
  }

  async extendThrDueDate(request: ThrDueDateUpdateRequest): Promise<ThrDueDateUpdateResponse> {
    const payload: Record<string, unknown> = {
      action: 'extend_due_date',
      handReceiptId: request.handReceiptId,
      hand_receipt_id: request.handReceiptId,
      newDueDate: request.newDueDate,
      new_due_date: request.newDueDate,
    };

    if (request.performedBy) {
      payload.performedBy = request.performedBy;
      payload.performed_by = request.performedBy;
    }

    if (request.note) {
      payload.note = request.note;
    }

    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_THR_UPDATE, payload);
  }

  async convertThrToPhr(request: ThrConversionRequest): Promise<ThrConversionResponse> {
    const payload: Record<string, unknown> = {
      action: 'convert_to_phr',
      handReceiptId: request.handReceiptId,
      hand_receipt_id: request.handReceiptId,
      approvalConfirmed: request.approvalConfirmed,
      approval_confirmed: request.approvalConfirmed,
    };

    if (request.performedBy) {
      payload.performedBy = request.performedBy;
      payload.performed_by = request.performedBy;
    }

    if (request.approvalReference) {
      payload.approvalReference = request.approvalReference;
      payload.approval_reference = request.approvalReference;
    }

    if (request.note) {
      payload.note = request.note;
    }

    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_THR_UPDATE, payload);
  }

  async convertAllocationToSizedAsset(
    request: AllocationSizeConversionRequest,
  ): Promise<AllocationSizeConversionResponse> {
    const payload: Record<string, unknown> = {
      action: 'convert_to_sized_asset',
      handReceiptId: request.handReceiptId,
      hand_receipt_id: request.handReceiptId,
      targetCatalogId: request.targetCatalogId,
      target_catalog_id: request.targetCatalogId,
    };

    if (request.performedBy) {
      payload.performedBy = request.performedBy;
      payload.performed_by = request.performedBy;
    }

    if (request.note) {
      payload.note = request.note;
    }

    if (request.justification) {
      payload.justification = request.justification;
    }

    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_ALLOCATION_UPDATE, payload);
  }

  async convertAllocationToSizedAssetBatch(
    request: AllocationSizeConversionBatchRequest,
  ): Promise<AllocationSizeConversionBatchResponse> {
    const payload: Record<string, unknown> = {
      action: 'convert_to_sized_asset',
      handReceiptIds: request.handReceiptIds,
      hand_receipt_ids: request.handReceiptIds,
      targetCatalogId: request.targetCatalogId,
      target_catalog_id: request.targetCatalogId,
    };

    if (request.performedBy) {
      payload.performedBy = request.performedBy;
      payload.performed_by = request.performedBy;
    }

    if (request.note) {
      payload.note = request.note;
    }

    if (request.justification) {
      payload.justification = request.justification;
    }

    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_ALLOCATION_UPDATE_BATCH, payload);
  }

  async updateIeKitRemovedAssets(
    request: IeKitRemovedAssetsRequest,
  ): Promise<IeKitRemovedAssetsResponse> {
    if (!request.removedCatalogIds.length) {
      return {
        ieKitId: request.ieKitId,
        removedCatalogIds: [],
        individualsUpdated: 0,
        handReceiptsUpdated: 0,
      };
    }

    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_IE_KIT_REMOVED_ASSETS, {
      ieKitId: request.ieKitId,
      ie_kit_id: request.ieKitId,
      removedCatalogIds: request.removedCatalogIds,
      removed_catalog_ids: request.removedCatalogIds,
    });
  }

  async updateIeKitQuantityDecreasedAssets(
    request: IeKitQuantityDecreasedAssetsRequest,
  ): Promise<IeKitQuantityDecreasedAssetsResponse> {
    if (!request.adjustments.length) {
      return {
        individualsUpdated: 0,
        handReceiptsUpdated: 0,
      };
    }

    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_IE_KIT_QUANTITY_DECREASED_ASSETS, {
      ieKitId: request.ieKitId,
      ie_kit_id: request.ieKitId,
      adjustments: request.adjustments.map((adjustment) => ({
        catalogId: adjustment.catalogId,
        catalog_id: adjustment.catalogId,
        newQuantity: adjustment.newQuantity,
        new_quantity: adjustment.newQuantity,
      })),
    });
  }

  async updateIeKitAddedAssets(
    request: IeKitAddedAssetsRequest,
  ): Promise<IeKitAddedAssetsResponse> {
    if (!request.addedCatalogIds.length) {
      return {
        ieKitId: request.ieKitId,
        addedCatalogIds: [],
        individualsUpdated: 0,
        handReceiptsUpdated: 0,
      };
    }

    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_IE_KIT_ADDED_ASSETS, {
      ieKitId: request.ieKitId,
      ie_kit_id: request.ieKitId,
      addedCatalogIds: request.addedCatalogIds,
      added_catalog_ids: request.addedCatalogIds,
    });
  }

  async updatePersonIeKitAssignment(
    request: PersonIeKitAssignmentRequest,
  ): Promise<PersonIeKitAssignmentResponse> {
    if (request.addedPersonIds.length === 0 && request.removedPersonIds.length === 0) {
      return {
        ieKitId: request.ieKitId,
        handReceiptsStamped: 0,
        handReceiptsCleared: 0,
      };
    }

    return httpClient.post(API_CONFIG.ENDPOINTS.IEM_IE_PERSON_ASSIGNMENT_UPDATED, {
      ieKitId: request.ieKitId,
      addedPersonIds: request.addedPersonIds,
      removedPersonIds: request.removedPersonIds,
    });
  }

  private async getAvailableAssetsFromView({
    filters,
    limit,
    order,
  }: {
    filters: Record<string, string>;
    limit?: number;
    order: string;
  }): Promise<Entity[]> {
    const params = new URLSearchParams({
      order,
    });

    if (typeof limit === 'number' && Number.isFinite(limit)) {
      params.set('limit', String(limit));
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      params.set(key, value);
    });

    const query = params.toString();
    return httpClient.get<Entity[]>(`/api/v1/iem_available_assets?${query}`);
  }
}
